import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { useSnackbar } from "notistack";
import { useAccessCatalog, useCreateUser } from "../../hooks/useAccessManagement";
import { ROLE_META } from "./roleMeta";
import { PRIMARY_BTN_SX, SECONDARY_BTN_SX, SECTION_LABEL_SX } from "./styles";

// Only internal accounts can sign in, and the roster only lists them (see
// buildUserFilter in routes/accessManagement.js) — so the domain isn't a free-text field,
// it's a fixed suffix on the local part the Super Admin types.
const EMAIL_DOMAIN = "@idiotic.media";

// Matches the server's own rule (PASSWORD_RULE in routes/accessManagement.js) so the
// drawer can say what's wrong before the round trip rather than after it.
const PASSWORD_RULE = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

const DEFAULT_BALANCE = 200; // same grant the backend defaults to

// Sanchit Jain. Every account created here is managed by him — the server adds this id to
// managedBy on every create whatever the drawer sends (ALWAYS_MANAGED_BY in
// routes/accessManagement.js), so it's pre-selected below and its chip has no delete icon
// rather than letting a click appear to remove something that would come straight back.
const ALWAYS_MANAGED_BY_ID = "642402bcf98aec403934f7c2";

// Omits the characters that get misread when a password is passed on verbally or in
// chat (O/0, l/1/I), since that's exactly how these credentials reach the new user.
const PASSWORD_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";

function generatePassword(length = 14) {
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  const body = Array.from(values, (v) => PASSWORD_ALPHABET[v % PASSWORD_ALPHABET.length]).join("");
  // getRandomValues gives no guarantee all three required classes turn up, so the rule is
  // met by construction — an uppercase letter, a lowercase one and a digit sit in fixed
  // slots around the random middle, rather than re-rolling until they happen to appear.
  return `A${body.slice(0, length - 3)}q7`;
}

// ── Spacing scale ────────────────────────────────────────────────────────────────────
// One scale for the whole drawer, so the gaps can't drift apart block by block the way
// they do when each section picks its own margins:
//   24px  drawer side gutter and header/footer padding
//   20px  card padding, and the gap between two fields
//   16px  gap between cards, and from a section's label to its first control
const GUTTER = { xs: 2, sm: 3 }; // MUI spacing units — 16px on a phone, 24px elsewhere
const CARD_PADDING = 2.5;
const FIELD_GAP = 2.5;
const CARD_GAP = 2;
const LABEL_GAP = 2;

// Chrome paints an autofilled input with its own blue-grey background via an inset
// box-shadow that `bgcolor` can't win against, and it fills fields without firing the
// events MUI's floating label listens to — which is what made the label sit on top of the
// value. Every field here therefore keeps its label permanently shrunk (see FIELD_PROPS)
// and repaints the autofill state back to white.
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "11px",
    // Resting fields are a hair off-white and only turn fully white on focus, which reads
    // as "this one is live" without needing a heavier border.
    bgcolor: "#FBFCFD",
    fontFamily: "Quicksand",
    fontWeight: 600,
    fontSize: 13.5,
    color: "#1C1C2E",
    transition: "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
    "& fieldset": { borderColor: "rgba(16,24,40,0.10)" },
    "&:hover fieldset": { borderColor: "rgba(16,24,40,0.20)" },
    "&.Mui-focused": { bgcolor: "#fff", boxShadow: "0 0 0 3px rgba(255,82,0,0.10)" },
    "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: 1.5 },
    "&.Mui-error fieldset": { borderColor: "#E34C14" },
  },
  "& input:-webkit-autofill, & input:-webkit-autofill:focus": {
    WebkitBoxShadow: "0 0 0 100px #fff inset",
    WebkitTextFillColor: "#1C1C2E",
    caretColor: "#1C1C2E",
    borderRadius: "11px",
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Quicksand",
    fontWeight: 700,
    fontSize: 12.5,
    color: "#6B7280",
    "&.Mui-focused": { color: "#FF5200" },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "Quicksand",
    fontWeight: 600,
    fontSize: 11,
    color: "#9CA3AF",
    mt: 0.75,
    mx: 0.25,
    "&.Mui-error": { color: "#E34C14" },
  },
  "& input::placeholder": { color: "#B9BEC6", opacity: 1, fontWeight: 600 },
};

// Taller than MUI's `small` default — 44px, which is what stops a column of inputs from
// looking cramped next to 20px card padding. Kept off the Autocompletes, whose own tag
// layout sets the height instead.
const TEXT_FIELD_SX = {
  ...FIELD_SX,
  "& .MuiOutlinedInput-input": { py: 1.4 },
};

// A shrunk label never overlaps a value, whatever put the value there — which is the one
// thing MUI's default floating label can't promise once the browser autofills a field.
// `autoComplete: "off"` asks Chrome not to in the first place, and the permanent shrink is
// the belt to that's braces — Chrome ignores the hint on anything that smells like a
// sign-up form, which this is.
const FIELD_PROPS = {
  size: "small",
  fullWidth: true,
  autoComplete: "off",
  InputLabelProps: { shrink: true },
  sx: TEXT_FIELD_SX,
};

const CARD_SX = {
  bgcolor: "#fff",
  border: "1px solid rgba(16,24,40,0.07)",
  borderRadius: "16px",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  p: CARD_PADDING,
};

const HINT_SX = {
  fontFamily: "Quicksand",
  fontWeight: 600,
  fontSize: 11.5,
  lineHeight: 1.55,
  color: "#9CA3AF",
};

// Every section is the same card with the same label row, so the label-to-content gap is
// identical in all five rather than set by hand each time.
function SectionCard({ label, action, children }) {
  return (
    <Box sx={CARD_SX}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: LABEL_GAP, minHeight: 26 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {/* Small gradient tick tying each section back to the app's orange. */}
          <Box
            sx={{
              width: 3,
              height: 14,
              borderRadius: "2px",
              background: "linear-gradient(180deg, #FF5200 0%, #FF8C00 100%)",
            }}
          />
          <Typography sx={SECTION_LABEL_SX}>{label}</Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

// Right-hand drawer behind the table's "New user" button. Creates the account outright —
// the same document that used to be hand-inserted — instead of only editing existing
// people like ManageAccessDialog does.
export default function NewUserDrawer({ open, allUsers, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const createUser = useCreateUser();
  const { data: catalogResponse } = useAccessCatalog();
  const catalog = catalogResponse?.data || [];

  const [fullName, setFullName] = useState("");
  const [emailLocal, setEmailLocal] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState("user");
  const [balance, setBalance] = useState(String(DEFAULT_BALANCE));
  const [permissions, setPermissions] = useState([]);
  const [manageUsers, setManageUsers] = useState([]);
  const [managedBy, setManagedBy] = useState([]);
  const [touched, setTouched] = useState(false); // only show errors once Create was pressed

  const groupedCatalog = useMemo(() => {
    const groups = {};
    for (const item of catalogResponse?.data || []) {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    }
    return groups;
  }, [catalogResponse]);

  const candidates = allUsers || [];
  const email = emailLocal ? `${emailLocal.trim()}${EMAIL_DOMAIN}` : "";

  // Depends on allUsers, not the `candidates` fallback — that expression builds a fresh []
  // every render while the roster is still loading, which would re-run this each time.
  //
  // allUsers is capped at 200 (useAllAccessUsers) — if the org ever grows past that and
  // Sanchit sorts outside the first page, this (and the chip below) resolves to null rather
  // than a stale placeholder object. That's a deliberate choice, not an oversight: the
  // backend adds ALWAYS_MANAGED_BY_ID unconditionally regardless of what this drawer sends
  // (routes/accessManagement.js), so the only thing that degrades is this preview, never the
  // actual data — a synthetic fallback here would have to be dropped again once the real
  // fetch resolves and found nothing, which is more state to get wrong for a cosmetic gap
  // that only shows up well past the org's current size.
  const defaultManager = useMemo(
    () => (allUsers || []).find((candidate) => candidate.id === ALWAYS_MANAGED_BY_ID) || null,
    [allUsers]
  );

  // Pre-selects the default manager once the roster it has to be looked up in has loaded —
  // which is usually a moment after the drawer opens, hence an effect rather than an
  // initial state value.
  useEffect(() => {
    if (!open || !defaultManager) return;
    setManagedBy((prev) =>
      prev.some((person) => person.id === defaultManager.id) ? prev : [defaultManager, ...prev]
    );
  }, [open, defaultManager]);

  const nameError = !fullName.trim() ? "Full name is required" : "";
  // The domain is appended for them, so the only thing that can be wrong here is the local
  // part — blank, or containing the parts of an address it can't contain.
  const emailError = !emailLocal.trim()
    ? "Email is required"
    : /[@\s]/.test(emailLocal.trim())
      ? `Type only the part before ${EMAIL_DOMAIN}`
      : "";
  const passwordError = !PASSWORD_RULE.test(password)
    ? "8+ characters with an uppercase letter, a lowercase letter and a number"
    : "";
  // `type="number"` + `inputProps={{ min: 0 }}` is decorative on its own — there's no <form>
  // here to trigger native constraint validation, and a browser happily lets "-" be typed
  // into a number input regardless. The backend already floors a negative value to 0, but
  // silently swapping in a number the Super Admin never asked for is worse than just saying
  // so before Create.
  const balanceError =
    balance.trim() !== "" && (!Number.isFinite(Number(balance)) || Number(balance) < 0)
      ? "Enter 0 or a positive number"
      : "";

  const resetForm = () => {
    setFullName("");
    setEmailLocal("");
    setPhoneNumber("");
    setPassword("");
    setShowPassword(false);
    setRole("user");
    setBalance(String(DEFAULT_BALANCE));
    setPermissions([]);
    setManageUsers([]);
    setManagedBy([]);
    setTouched(false);
  };

  const handleClose = () => {
    if (createUser.isLoading) return;
    resetForm();
    onClose(false);
  };

  const togglePermission = (key) =>
    setPermissions((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`);
      enqueueSnackbar("Credentials copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy — select and copy the password manually", { variant: "warning" });
    }
  };

  const handleCreate = async () => {
    setTouched(true);
    if (nameError || emailError || passwordError || balanceError) return;

    try {
      const response = await createUser.mutateAsync({
        fullName: fullName.trim(),
        email,
        phoneNumber: phoneNumber.trim(),
        password,
        role,
        // A Super Admin is full-access regardless, and an empty list means "not restricted
        // yet" — both are exactly what the backend does with them, so neither needs special
        // casing beyond not sending a checklist that would be ignored.
        permissions: role === "superadmin" ? [] : permissions,
        manageUsers: manageUsers.map((u) => u.id),
        managedBy: managedBy.map((u) => u.id),
        balance: Number(balance) || 0,
      });

      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }

      enqueueSnackbar(`${fullName.trim()} added — share their password now, it isn't shown again`, {
        variant: "success",
      });
      resetForm();
      onClose(true);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not create the user", { variant: "error" });
    }
  };

  const restrictedCount = permissions.length;
  const fullAccess = role === "superadmin" || restrictedCount === 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 500 },
          bgcolor: "#F6F7F9",
          // Tells a forced-dark browser (Chrome's auto dark mode, and any extension that
          // respects the property) that this surface is already designed light and must not
          // be inverted — otherwise the whole drawer comes back as dark grey with the white
          // cards turned to near-black, which is not a palette anything here was styled for.
          colorScheme: "light",
          borderLeft: "none",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: GUTTER,
          py: 2.25,
          background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
          color: "#fff",
          boxShadow: "0 6px 20px rgba(255,82,0,0.18)",
          zIndex: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.30)",
            }}
          >
            <PersonAddAltOutlinedIcon sx={{ fontSize: 21, color: "#fff" }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 17, lineHeight: 1.25, letterSpacing: 0.1 }}
            >
              New user
            </Typography>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 11.5, opacity: 0.92 }}>
              They can sign in as soon as you hit create
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={createUser.isLoading}
            sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.18)" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Form ── */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: GUTTER,
          py: 2.5,
          // A slim scrollbar, so the drawer's own doesn't sit next to the page's as two
          // competing full-width bars.
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(16,24,40,0.14)",
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": { bgcolor: "rgba(16,24,40,0.26)", backgroundClip: "content-box" },
        }}
      >
        <Stack spacing={CARD_GAP}>
          <SectionCard label="Identity">
            <Stack spacing={FIELD_GAP}>
              <TextField
                {...FIELD_PROPS}
                label=""
                placeholder="FullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                error={touched && Boolean(nameError)}
                helperText={touched && nameError ? nameError : ""}
              />
              <TextField
                {...FIELD_PROPS}
                label="Email"
                placeholder=""
                value={emailLocal}
                onChange={(e) => setEmailLocal(e.target.value)}
                error={touched && Boolean(emailError)}
                helperText={touched && emailError ? emailError : "Only internal addresses can sign in"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        sx={{
                          fontFamily: "Quicksand",
                          fontWeight: 700,
                          fontSize: 12.5,
                          color: "#9CA3AF",
                          bgcolor: "rgba(16,24,40,0.04)",
                          borderRadius: "7px",
                          px: 1,
                          py: 0.35,
                        }}
                      >
                        {EMAIL_DOMAIN}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                {...FIELD_PROPS}
                label="Phone number"
                placeholder="Optional"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </Stack>
          </SectionCard>

          <SectionCard
            label="Password"
            action={
              <Button
                size="small"
                startIcon={<AutorenewIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setPassword(generatePassword());
                  setShowPassword(true);
                }}
                sx={{
                  textTransform: "none",
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#FF5200",
                  borderRadius: "8px",
                  px: 1.25,
                  "&:hover": { bgcolor: "rgba(255,82,0,0.08)" },
                }}
              >
                Generate
              </Button>
            }
          >
            <TextField
              {...FIELD_PROPS}
              label="Password"
              placeholder="Type or generate one"
              // Chrome's password manager treats a bare type="password" as a login form and
              // offers to fill it — "new-password" is the one value it honours here.
              autoComplete="new-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={touched && Boolean(passwordError)}
              helperText={
                touched && passwordError
                  ? passwordError
                  : "Hashed on the server — copy it now, it can't be read back"
              }
              InputProps={{
                sx: { fontFamily: showPassword ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "Quicksand" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title={showPassword ? "Hide" : "Show"}>
                        <IconButton size="small" onClick={() => setShowPassword((prev) => !prev)}>
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy email and password">
                        <span>
                          <IconButton
                            size="small"
                            onClick={copyCredentials}
                            // Also gated on passwordError, not just non-empty — otherwise a
                            // password that fails the server's own rule can still be copied
                            // and handed to the new hire before Create ever rejects it.
                            disabled={!password || !emailLocal.trim() || Boolean(passwordError)}
                          >
                            <ContentCopyIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />
          </SectionCard>

          <SectionCard label="Role & credits">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Select
                size="small"
                fullWidth
                value={role}
                onChange={(e) => setRole(e.target.value)}
                sx={{
                  borderRadius: "11px",
                  bgcolor: "#FBFCFD",
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: 13.5,
                  color: "#1C1C2E",
                  "& .MuiSelect-select": { py: 1.4 },
                  "& fieldset": { borderColor: "rgba(16,24,40,0.10)" },
                  "&:hover fieldset": { borderColor: "rgba(16,24,40,0.20)" },
                  "&.Mui-focused": { bgcolor: "#fff" },
                  "&.Mui-focused fieldset": { borderColor: "#FF5200" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "12px",
                      border: "1px solid rgba(16,24,40,0.07)",
                      boxShadow: "0 12px 32px rgba(16,24,40,0.12)",
                    },
                  },
                }}
              >
                {Object.entries(ROLE_META).map(([value, meta]) => (
                  <MenuItem key={value} value={value} sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 13.5 }}>
                    {meta.label}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                {...FIELD_PROPS}
                label="Credits"
                type="number"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                inputProps={{ min: 0 }}
                error={touched && Boolean(balanceError)}
                helperText={touched && balanceError ? balanceError : " "}
                sx={{ ...TEXT_FIELD_SX, width: { xs: "100%", sm: 132 }, flexShrink: 0 }}
              />
            </Stack>
          </SectionCard>

          <SectionCard
            label="Section access"
            action={
              <Chip
                size="small"
                label={fullAccess ? "Full access" : `${restrictedCount} of ${catalog.length} sections`}
                sx={{
                  height: 22,
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: 11,
                  bgcolor: fullAccess ? "rgba(9,172,49,0.10)" : "rgba(255,82,0,0.10)",
                  color: fullAccess ? "#09AC31" : "#FF5200",
                }}
              />
            }
          >
            {role === "superadmin" ? (
              <Typography sx={HINT_SX}>Super Admins always reach every section and can't be restricted.</Typography>
            ) : (
              <>
                <Typography sx={{ ...HINT_SX, mb: 1.5 }}>
                  Leave everything unchecked for full access — you can restrict them later from their
                  Manage dialog.
                </Typography>
                <Box
                  sx={{
                    maxHeight: 240,
                    overflowY: "auto",
                    pr: 1,
                    // The checklist is the one part of this form that can get long, so it
                    // scrolls inside its own card rather than pushing everything below it
                    // (hierarchy, and the sense of how much form is left) off the screen.
                    "&::-webkit-scrollbar": { width: 5 },
                    "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(16,24,40,0.14)", borderRadius: 5 },
                  }}
                >
                  <Stack spacing={1.5}>
                    {Object.entries(groupedCatalog).map(([group, items]) => (
                      <Box key={group}>
                        <Typography
                          sx={{
                            fontFamily: "Quicksand",
                            fontWeight: 800,
                            fontSize: 10,
                            color: "#B0B5BD",
                            textTransform: "uppercase",
                            letterSpacing: 0.9,
                            mb: 0.5,
                          }}
                        >
                          {group}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            // Two columns need ~200px each to keep a label like
                            // "Payments — Campaigns" on one line; a phone has room for one.
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            columnGap: 1,
                            rowGap: 0.25,
                          }}
                        >
                          {items.map((item) => (
                            <FormControlLabel
                              key={item.key}
                              sx={{ ml: 0, mr: 0, borderRadius: "8px", "&:hover": { bgcolor: "rgba(16,24,40,0.03)" } }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={permissions.includes(item.key)}
                                  onChange={() => togglePermission(item.key)}
                                  sx={{ py: 0.5, color: "#CBD0D6", "&.Mui-checked": { color: "#FF5200" } }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 12.5, color: "#374151" }}
                                >
                                  {item.label}
                                </Typography>
                              }
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </SectionCard>

          <SectionCard label="Hierarchy">
            <Typography sx={{ ...HINT_SX, mb: 2 }}>
              Every new user is managed by {defaultManager?.fullName || "Sanchit Jain"} — add anyone else here.
              "Managed by" widens what this user sees, "Manages" only does anything for an Admin. Both are
              changeable later from the table.
            </Typography>
            <Stack spacing={FIELD_GAP}>
              <Autocomplete
                multiple
                size="small"
                options={candidates}
                value={managedBy}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.fullName || option.email}
                // Backspace clears the last tag and the "clear" button empties the field, so
                // the default manager is put back here rather than only in renderTags.
                onChange={(_, selected) =>
                  setManagedBy(
                    defaultManager && !selected.some((person) => person.id === defaultManager.id)
                      ? [defaultManager, ...selected]
                      : selected
                  )
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { onDelete, ...tagProps } = getTagProps({ index });
                    const locked = option.id === ALWAYS_MANAGED_BY_ID;
                    return (
                      <Chip
                        size="small"
                        label={option.fullName}
                        {...tagProps}
                        onDelete={locked ? undefined : onDelete}
                        key={option.id}
                        sx={{
                          fontFamily: "Quicksand",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 24,
                          ...(locked
                            ? { bgcolor: "rgba(255,82,0,0.10)", color: "#FF5200" }
                            : { bgcolor: "rgba(16,24,40,0.06)", color: "#374151" }),
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Managed by"
                    placeholder="Search by name"
                    // params carries the label's own id/htmlFor wiring, so the shared props
                    // are merged into it rather than spread over the top of it.
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    sx={FIELD_SX}
                  />
                )}
              />
              <Autocomplete
                multiple
                size="small"
                options={candidates}
                value={manageUsers}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.fullName || option.email}
                onChange={(_, selected) => setManageUsers(selected)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option.fullName}
                      {...getTagProps({ index })}
                      key={option.id}
                      sx={{
                        fontFamily: "Quicksand",
                        fontWeight: 700,
                        fontSize: 11,
                        height: 24,
                        bgcolor: "rgba(16,24,40,0.06)",
                        color: "#374151",
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manages these users"
                    placeholder="Search by name"
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    sx={FIELD_SX}
                  />
                )}
              />
            </Stack>
          </SectionCard>
        </Stack>
      </Box>

      {/* ── Footer ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: GUTTER,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid rgba(16,24,40,0.07)",
          boxShadow: "0 -6px 20px rgba(16,24,40,0.05)",
        }}
      >
        {/* The assembled address, so it can be checked without scrolling back up. */}
        <Typography
          sx={{
            ...HINT_SX,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: { xs: "none", sm: "block" },
          }}
        >
          {email || "Name, email and a password to continue"}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button onClick={handleClose} disabled={createUser.isLoading} sx={{ ...SECONDARY_BTN_SX, px: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createUser.isLoading}
            sx={{ ...PRIMARY_BTN_SX, px: 2.5, py: 1 }}
          >
            {createUser.isLoading ? "Creating…" : "Create user"}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
